import { useTranslation } from 'react-i18next';
import { RefObject, useEffect, useMemo, useState } from 'react';
import useCoordinates, { CENTRAL_COORDS } from '../../hooks/useCoordinates';
import { useKeycloakReady } from 'hooks/useKeycloakReady';
import { MapDataUtils } from '../../utils/mapData';
import { NetworkList } from '../NetworkList';
import CustomGoogleMap from '../GoogleMap';
import styled from 'styled-components';
import { useSearchHospitalWithPage } from '../../hooks/useSearchHospitalWithPage';
import Filters from '../filters/Filters';
import { FullWidthSpace } from 'components';
import { InputRef, Result } from 'antd';
import { breakpoint } from 'styled-components-breakpoint';
import { useBookmarkContext } from '../../../../contexts/bookmark';
import { ALL_AREA, ALL_DISTRICTS } from '../../../../constants/areaNDistricts';

const ListItemNMapWrapper = styled.div`
  display: grid;
  ${breakpoint('tablet')`
    grid-template-columns: 425px 2fr;
    grid-column-gap: 24px;
  `}
`;

type HospitalTabPaneProp = {
  inputEl: RefObject<InputRef>;
  wrapperRef?: RefObject<HTMLDivElement>;
};

export const HospitalTabPane = ({ inputEl, wrapperRef }: HospitalTabPaneProp): JSX.Element => {
  const { t } = useTranslation();
  const { bookmark } = useBookmarkContext();
  const [searchQuery, setSearchQuery] = useState<string>(inputEl?.current?.input?.value ?? '');
  const [specialties, setSpecialties] = useState<string[]>(['General Practitioner'.toUpperCase()]);
  const [area, setArea] = useState<string | undefined>(undefined);
  const [districts, setDistricts] = useState<string[] | undefined>(undefined);
  const { coords } = useCoordinates();
  const [googleMapCoords, setGoogleMapCoords] = useState<google.maps.LatLngLiteral | undefined>({
    lat: CENTRAL_COORDS.latitude,
    lng: CENTRAL_COORDS.longitude,
  });

  const { ready, token } = useKeycloakReady();
  const [page, setPage] = useState<number>(1);
  const [currentItemId, setCurrentItemId] = useState<number | string | undefined>(undefined);
  const PAGE_SIZE = 5;
  const allDistrictsSelected = useMemo(() => {
    return districts?.length && districts.includes(ALL_DISTRICTS);
  }, [districts]);
  const shouldUseCoordinates = useMemo(() => {
    if (area === ALL_AREA || area === '') return false;
    return allDistrictsSelected;
  }, [allDistrictsSelected, area]);

  const params = {
    latitude: shouldUseCoordinates ? undefined : coords?.latitude,
    longitude: shouldUseCoordinates ? undefined : coords?.longitude,
    radius: coords?.radius,
    searchQuery,
    area: allDistrictsSelected ? undefined : area,
    districts: allDistrictsSelected ? undefined : districts,
    pageIndex: page,
    pageSize: PAGE_SIZE,
  };

  const { data, shouldFetch, mutate } = useSearchHospitalWithPage({
    key: {
      key: 'searchHospital',
      params,
    },
    accessToken: token || '',
    ...params,
  });

  useEffect(() => {
    wrapperRef?.current?.scrollIntoView();
  }, [page, wrapperRef]);

  useEffect(() => {
    setGoogleMapCoords({ lat: coords?.latitude ?? 0, lng: coords?.longitude ?? 0 });
    if (ready) {
      shouldFetch(true);
      mutate();
    }
  }, [ready, coords, shouldFetch, mutate]);

  const mappedData = useMemo(() => {
    console.log(data);
    return (data?.results || []).map((a) => {
      const bm = bookmark?.find((m) => m.data?.hospitalId === a.hospitalId);
      return MapDataUtils.mapData(a, bm?.bookmarkId);
    });
  }, [bookmark, data]);

  useEffect(() => {
    if (mappedData.length) {
      const currentItem = mappedData[0];
      setCurrentItemId(currentItem.itemId);
      setGoogleMapCoords({
        lat: currentItem.latitude ?? 0,
        lng: currentItem.longitude ?? 0,
      });
    }
  }, [mappedData]);

  const handleOnListItemSelected = (rowId: number | string, latitude: number, longitude: number) => {
    setCurrentItemId(rowId);
    setGoogleMapCoords({ lat: latitude, lng: longitude });
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery, districts]);

  return (
    <FullWidthSpace direction="vertical" size="middle">
      <Filters
        specialtyValue={specialties}
        handleOnSpecialtyChange={setSpecialties}
        districtValue={districts}
        handleOnDistrictChange={setDistricts}
        handleOnAreaChange={setArea}
        handleOnSearchQueryChange={setSearchQuery}
        showSpecialty={false}
        inputEl={inputEl}
      />
      {data?.totalCount ? (
        <ListItemNMapWrapper>
          <NetworkList
            setPage={setPage}
            page={page}
            pageSize={PAGE_SIZE}
            total={data?.totalCount}
            items={mappedData}
            handleOnListItemSelected={handleOnListItemSelected}
            currentRowId={currentItemId}
            searchQuery={searchQuery}
          />
          <div>
            <CustomGoogleMap location={googleMapCoords} data={mappedData} handleOnMarkerClick={setCurrentItemId} />
          </div>
        </ListItemNMapWrapper>
      ) : (
        <Result icon={<></>} title={t('panelList.noResults')} subTitle={t('panelList.tryFiltering')} />
      )}
    </FullWidthSpace>
  );
};
